// src/portal/PortalApproveControl.jsx
//
// APPROVE. One POST, no body, and TERMINAL — there is no reopen in v1, and
// `plan_reviews_approved_terminal_chk` refuses to move the row afterwards.
//
// ── THE BUTTON IS NOT OFFERED TO SOMEONE ROUTE 12 WILL REFUSE ───────────────
//
// requireOrgAdmin answers a bare `403 {"error":"Forbidden"}` with no code —
// measured in authz-org-admin.probe.mjs §3. In the portal's taxonomy (§3.18)
// that is `shape: "uncoded"` -> `unattributed`, which renders "Something went
// wrong, and we can't tell you what". For a permission question the server can
// answer exactly. It is the only refusal in this feature with nothing behind
// it, and it would be the worst-rendering one.
//
// So the decision comes from route 6's `viewer_may_approve`, computed by the
// SAME composition requireOrgAdmin applies. Not offered is better than offered
// and refused illegibly — and the reason is stated, because a control that is
// simply missing is its own kind of confusing.
//
// ── CONFIRMED, BECAUSE IT CANNOT BE UNDONE ─────────────────────────────────
//
// Approval is terminal for the review, for its markup, and for its thread:
// notes are refused once approved. The reviewer is told that while they can
// still decline.

import { useState } from "react";
import { approvePlanReview } from "../api/portalApi";
import { dispositionFor } from "../api/portalRefusals";
import { copyFor } from "../api/portalRefusalCopy";

export default function PortalApproveControl({ planReviewId, mayApprove, onApproved, variant = "" }) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [approved, setApproved] = useState(null);

  if (!mayApprove) {
    return (
      <p className="text-[11px] text-fg-secondary" data-testid="approve-not-permitted">
        Approving a plan review is limited to organization administrators.
      </p>
    );
  }

  async function doApprove() {
    setBusy(true);
    setFailure(null);
    try {
      const res = await approvePlanReview(planReviewId);
      // The server's own record of who approved and when — not the local clock
      // and not the username we happen to hold.
      setApproved({ at: res?.approved_at ?? null, by: res?.approved_by ?? null });
      setConfirming(false);
      onApproved?.();
    } catch (e) {
      setFailure(copyFor(e, dispositionFor(e)));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  if (approved) {
    return (
      <p className="text-xs" data-testid="approve-done">
        Approved{approved.by ? ` by ${approved.by}` : ""}
        {approved.at ? ` on ${new Date(approved.at).toLocaleString()}` : ""}.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1" data-testid="approve-control">
      {failure && (
        <div role="alert" className="rounded border border-bg-border bg-bg-soft px-3 py-2">
          <p className="text-sm font-medium">{failure.title}</p>
          {failure.detail && <p className="mt-1 text-xs text-fg-secondary">{failure.detail}</p>}
        </div>
      )}

      {confirming ? (
        <div className="flex flex-col gap-1" data-testid="approve-confirm">
          <p className="text-xs">
            Approve this plan? It closes the review for good — no further findings, revisions or
            notes.
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={doApprove} disabled={busy}
              data-testid="approve-confirm-yes"
              className="rounded border border-bg-border px-2 py-1 text-xs disabled:opacity-50">
              {busy ? "Approving…" : "Approve the plan"}
            </button>
            <button type="button" onClick={() => setConfirming(false)} disabled={busy}
              data-testid="approve-confirm-no"
              className="rounded border border-bg-border px-2 py-1 text-xs disabled:opacity-50">
              Not yet
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setConfirming(true)}
          data-testid="approve-plan"
          className={`self-start ${variant || "rounded border border-bg-border px-2 py-1 text-xs"}`}>
          Approve the plan
        </button>
      )}
    </div>
  );
}
